import { useCallback, useEffect, useRef, useState } from 'react';
import { RichText } from '../i18n/RichText';
import { formatDateTime } from '../i18n/format';
import { currentUiLang, withUiLang } from '../i18n/feature';
import { caughtErrorMessage } from '../i18n/server-errors';
import { getCurrentLocale, t } from '../services/language-manager';
import { getOrgActionDeliveries, type ActionDelivery } from '../services/wizard-backend';
import { isSendFiles } from '../services/action-types';
import { copyText } from '../services/clipboard';

interface RecentDeliveriesCardProps {
  genesysOrgId: string;
  genesysRegion: string;
  /** actionId → display label of the currently configured instances. */
  actionLabels: Record<string, string>;
  /** actionId → type of the currently configured instances; a send-files row counts files, not items. */
  actionTypes: Record<string, string>;
  /** Insights dashboard URL (from App state); the footer falls back to plain text when empty. */
  insightsUrl?: string;
  /** Dev preview: sample rows, no API. */
  preview?: boolean;
}

/** Toolbar admin API max; the 7-day window is applied after fetch. */
const DELIVERIES_FETCH_LIMIT = 50;

function relativeTimeText(submittedAt: string): string {
  const ts = Date.parse(submittedAt);
  if (!Number.isFinite(ts)) return '';
  const elapsedMs = Date.now() - ts;
  if (elapsedMs < 60_000) return t('settings-actions-deliveries-just-now');
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 60) return t('settings-actions-deliveries-min-ago', { count: String(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1
      ? t('settings-actions-deliveries-hour-ago')
      : t('settings-actions-deliveries-hours-ago', { count: String(hours) });
  }
  const days = Math.floor(hours / 24);
  if (days === 1) return t('settings-actions-deliveries-yesterday');
  return t('settings-actions-deliveries-days-ago', { count: String(days) });
}

/** The first 8 characters identify an event when read aloud to support; Copy takes the full id. */
function shortEventId(eventId: string): string {
  return eventId.length > 12 ? `${eventId.slice(0, 8)}…` : eventId;
}

/** failureCode → human-readable badge text; unknown codes fall back to plain "Failed". */
const FAILURE_TEXT_KEYS: Record<string, string> = {
  WEBHOOK_TIMEOUT: 'settings-actions-deliveries-failed-timeout',
  WEBHOOK_FAILED: 'settings-actions-deliveries-failed-webhook',
  AUTH_FAILED: 'settings-actions-deliveries-failed-auth',
  VALIDATION_FAILED: 'settings-actions-deliveries-failed-validation',
};

function failureBadgeText(failureCode: string | null): string {
  const key = failureCode ? FAILURE_TEXT_KEYS[failureCode] : undefined;
  return key ? t(key) : t('settings-actions-deliveries-badge-failed');
}

/**
 * Recent webhook deliveries for the org, most recent first, last seven days.
 * Positioned as an integration diagnostic (did the endpoint receive the test /
 * first real delivery; eventId + failureCode to quote to support), not as a
 * review surface: submitted actions are reviewed in Mediabriz Insights.
 * Read-only: the backend records identifiers and counts only (no payloads,
 * customer data, or URLs). Rows carry status delivered/failed; legacy rows
 * without a status attribute are delivered. A retried action can legitimately
 * appear as one failed row plus one delivered row.
 */
export function RecentDeliveriesCard({
  genesysOrgId,
  genesysRegion,
  actionLabels,
  actionTypes,
  insightsUrl,
  preview = false,
}: RecentDeliveriesCardProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<ActionDelivery[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copiedTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
  }, []);

  const copyEventId = async (rowKey: string, eventId: string) => {
    await copyText(eventId);
    setCopiedKey(rowKey);
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopiedKey(null), 2000);
  };

  const load = useCallback(async () => {
    if (preview) {
      setDeliveries([{
        submittedAt: new Date().toISOString(),
        actionId: 'preview-order',
        eventId: 'evt_preview_001',
        itemCount: 3,
        status: 'delivered',
        failureCode: null,
        agentName: 'Alex Rivera',
        referenceId: 'PO-1001',
      }]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await getOrgActionDeliveries(genesysOrgId, genesysRegion, DELIVERIES_FETCH_LIMIT);
      setDeliveries(rows);
    } catch (e) {
      setError(caughtErrorMessage(e, 'settings-actions-deliveries-error'));
    } finally {
      setLoading(false);
    }
  }, [genesysOrgId, genesysRegion, preview]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    // A log, not an action: quiet panel, "Activity" label, timestamp-first rows with a status dot.
    <section className="deliveries-log" aria-labelledby="deliveries-log-title">
      <div className="deliveries-log__header">
        <div className="deliveries-log__heading">
          <span className="deliveries-log__eyebrow">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            {t('settings-actions-deliveries-eyebrow')}
          </span>
          <span id="deliveries-log-title" className="deliveries-log__title">{t('settings-actions-deliveries-title')}</span>
        </div>
        <button type="button" className="btn btn--secondary btn--small" onClick={() => void load()} disabled={loading}>
          {t('settings-actions-deliveries-refresh')}
        </button>
      </div>

      <p className="deliveries-log__intro">{t('settings-actions-deliveries-intro')}</p>

      {loading && <p className="settings-page__status">{t('settings-actions-deliveries-loading')}</p>}

      {!loading && error && (
        <p className="settings-page__error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && deliveries.length === 0 && (
        <p className="settings-page__status">{t('settings-actions-deliveries-empty')}</p>
      )}

      {!loading && !error && deliveries.length > 0 && (
        <div
          className="deliveries-list-wrap"
          tabIndex={0}
          role="region"
          aria-label={t('settings-actions-deliveries-title')}
        >
        <ul className="deliveries-list">
          {deliveries.map((delivery) => {
            // Deleted instances have no label; the raw actionId is still what support needs.
            const knownLabel = actionLabels[delivery.actionId];
            const label = knownLabel || delivery.actionId;
            const labelTitle = knownLabel
              ? knownLabel
              : t('settings-actions-deliveries-unknown-label', { id: delivery.actionId });
            const delivered = delivery.status === 'delivered';
            const countsFiles = isSendFiles(actionTypes[delivery.actionId] || '');
            const rowKey = `${delivery.eventId || delivery.actionId}-${delivery.status}-${delivery.submittedAt}`;
            const context = [
              delivery.agentName
                ? t('settings-actions-deliveries-agent', { agent: delivery.agentName })
                : '',
              delivered && delivery.referenceId
                ? t('settings-actions-deliveries-reference', { reference: delivery.referenceId })
                : '',
            ]
              .filter(Boolean)
              .join(' · ');
            const timeText = relativeTimeText(delivery.submittedAt);
            const absoluteTime = Number.isFinite(Date.parse(delivery.submittedAt))
              ? formatDateTime(new Date(delivery.submittedAt), getCurrentLocale())
              : '';
            return (
              // A retried action appears as a failed row plus a delivered row that
              // can share an eventId, so the key includes status and time.
              <li key={rowKey} className="delivery-row">
                <span className="delivery-row__time" title={absoluteTime || undefined}>
                  {timeText}
                </span>
                <span className="delivery-row__main">
                  <span className="delivery-row__label" dir="auto" title={labelTitle}>
                    {label}
                  </span>
                  {context && (
                    <span className="delivery-row__context" title={context}>
                      {context}
                    </span>
                  )}
                  {(delivery.eventId || (!delivered && delivery.failureCode)) && (
                    <span className="delivery-row__ref">
                      {delivery.eventId && (
                        <>
                          <span title={delivery.eventId}>
                            {t('settings-actions-deliveries-event-id')} {shortEventId(delivery.eventId)}
                          </span>
                          <button
                            type="button"
                            className="delivery-row__copy"
                            onClick={() => void copyEventId(rowKey, delivery.eventId)}
                            aria-label={copiedKey === rowKey
                              ? t('settings-actions-deliveries-copied')
                              : t('settings-actions-deliveries-copy-event')}
                            title={copiedKey === rowKey
                              ? t('settings-actions-deliveries-copied')
                              : t('settings-actions-deliveries-copy-event')}
                          >
                            {copiedKey === rowKey ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="9" y="9" width="13" height="13" rx="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            )}
                          </button>
                        </>
                      )}
                      {!delivered && delivery.failureCode && (
                        <span>{delivery.eventId ? ' · ' : ''}{delivery.failureCode}</span>
                      )}
                    </span>
                  )}
                </span>
                <span className="delivery-row__meta">
                  {delivery.itemCount !== null && (
                    <span className="delivery-row__items">
                      {countsFiles
                        ? (delivery.itemCount === 1
                          ? t('settings-actions-deliveries-file-one')
                          : t('settings-actions-deliveries-files', { count: String(delivery.itemCount) }))
                        : (delivery.itemCount === 1
                          ? t('settings-actions-deliveries-item-one')
                          : t('settings-actions-deliveries-items', { count: String(delivery.itemCount) }))}
                    </span>
                  )}
                  <span className={`delivery-row__status delivery-row__status--${delivered ? 'delivered' : 'failed'}`}>
                    {delivered
                      ? t('settings-actions-deliveries-badge-delivered')
                      : failureBadgeText(delivery.failureCode)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
        </div>
      )}

      <p className="deliveries-log__footnote">
        {/* Named window target: a second click re-focuses the open Insights tab. */}
        <RichText
          k="settings-actions-deliveries-review"
          values={{
            insights: insightsUrl ? (
              <a
            href={withUiLang(insightsUrl, currentUiLang())}
            target="mediabriz-insights"
            rel="noopener noreferrer"
            onClick={(event) => {
              event.preventDefault();
              window.open(withUiLang(insightsUrl, currentUiLang()), 'mediabriz-insights', 'noopener,noreferrer');
            }}
          >
                {t('settings-actions-deliveries-review-link')}
              </a>
            ) : (
              t('settings-actions-deliveries-review-link')
            ),
          }}
        />
      </p>
    </section>
  );
}
